import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import {
  distributionOptionsForCount,
  normalizeDesktopColumnLayout,
} from 'src/helpers/columnDistribution';
import LightTooltip from 'src/common/components/styled/LightTooltip';
import { ColumnsDesktopLayout } from 'src/interfaces/PageColumn';
import ColumnDistributionIcon from 'src/pages/Dashboard/System/Pages/components/ColumnDistributionIcon';

interface Props {
  columnCount: number;
  value: ColumnsDesktopLayout | undefined;
  onChange: (value: ColumnsDesktopLayout) => void;
  disabled?: boolean;
}

export default function ColumnDistributionPicker({
  columnCount,
  value,
  onChange,
  disabled = false,
}: Props) {
  const { t } = useTranslation();
  const selected = normalizeDesktopColumnLayout(columnCount, value);
  const options = distributionOptionsForCount(columnCount);

  return (
    <Box data-testid="column-distribution-picker" sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
        {t('system.pages.columnsDistribution')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {options.map(option => {
          const isSelected = selected === option.id;
          const label = t(`system.pages.${option.labelKey}`);
          return (
            <LightTooltip key={option.id} title={label} placement="top">
              <Box
                component="button"
                type="button"
                disabled={disabled}
                aria-label={label}
                aria-pressed={isSelected}
                onClick={() => onChange(option.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 56,
                  height: 48,
                  p: 1,
                  border: 2,
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  bgcolor: isSelected ? 'action.selected' : 'background.paper',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.5 : 1,
                  '&:hover': disabled
                    ? undefined
                    : {
                        bgcolor: isSelected ? 'action.selected' : 'action.hover',
                      },
                }}
              >
                <ColumnDistributionIcon spans={option.spans} selected={isSelected} />
              </Box>
            </LightTooltip>
          );
        })}
      </Box>
    </Box>
  );
}
